/**
 * Input.
 *
 * Turns keys and mouse into intent. Nothing here knows about the network, and
 * nothing here changes the world; it produces a direction and a set of events, and
 * the game loop decides what to do with them.
 */

import type { CameraController, GameSettings } from './camera';

export interface InputEvents {
  /** Left click. The game decides what is under it and what that means. */
  onClick(screenX: number, screenY: number): void;
  /**
   * A press, drag and release with the left button, for drawing out an area.
   *
   * Answering false to the first means the drag is not wanted, and the press
   * becomes an ordinary click when the button comes up.
   */
  onAreaStart(screenX: number, screenY: number): boolean;
  onAreaMove(screenX: number, screenY: number): void;
  onAreaEnd(screenX: number, screenY: number): void;
  /** Right click, or a long press. Opens the menu for whatever is under it. */
  onContextMenu(screenX: number, screenY: number): void;
  /** The pointer moved. Used to name whatever is under it. */
  onHover(screenX: number, screenY: number): void;
  onToggleRun(): void;
  onOpenChat(): void;
}

/**
 * The keys that move the view.
 *
 * They moved the character once. Walking is clicking where to walk now, and these
 * are what the editor pans with, which is the one place holding a direction still
 * makes sense.
 */
const MOVEMENT_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD']);

/**
 * Keys that turn the camera rather than the character.
 *
 * The arrows swing the view around and raise or lower the angle, which is what
 * they do in the game this takes after. Movement stays on the letters.
 */
const CAMERA_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/** Radians per second a held arrow turns the camera. */
const KEY_TURN_SPEED = 1.7;
const KEY_PITCH_SPEED = 1.1;

/** A finger held this long without moving opens the menu, standing in for a right click. */
const LONG_PRESS_MS = 420;

/** Movement past this cancels a long press and becomes a camera drag, in pixels. */
const TOUCH_DRAG_SLOP = 12;

/** How much a pinch has to change before it counts as a zoom, in pixels. */
const PINCH_SLOP = 6;

/** How often the thing under the pointer is worked out, in milliseconds. */
const HOVER_INTERVAL_MS = 60;

export class InputController {
  private readonly held = new Set<string>();

  /** True while a text field has focus, so typing does not walk the character. */
  private typing = false;

  /**
   * Whether the modifier for adding to a selection is down.
   *
   * Read by the editor when a click lands. Ctrl is the one every editor uses for
   * this, and it is not bound to anything else here.
   */
  addingToSelection = false;

  /**
   * Whether the modifier for taking things out of a selection is down.
   *
   * Shift, for the same reason Ctrl adds: it is what every editor uses, and
   * neither of them is bound to anything else here.
   */
  removingFromSelection = false;

  private rightButtonDown = false;
  private drawingArea = false;
  private lastHover = 0;
  private dragged = false;

  /** Fingers currently down on the world, by pointer id. */
  private readonly touches = new Map<number, { x: number; y: number }>();
  private touchStart: { x: number; y: number; time: number } | null = null;
  private longPressTimer: number | null = null;
  private longPressFired = false;
  private pinchDistance = 0;

  private settings: GameSettings;
  private readonly listeners: (() => void)[] = [];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: CameraController,
    private readonly events: InputEvents,
    settings: GameSettings,
  ) {
    this.settings = settings;
    this.attach();
  }

  apply(settings: GameSettings): void {
    this.settings = settings;
  }

  setTyping(typing: boolean): void {
    this.typing = typing;
    // Keys held when the chat box opened would otherwise stay held forever.
    if (typing) this.held.clear();
  }

  dispose(): void {
    this.cancelLongPress();
    for (const remove of this.listeners) remove();
    this.listeners.length = 0;
  }

  /**
   * Held direction in world space, already rotated into the camera's frame.
   *
   * Zero when nothing is held. Read by the editor to pan the view, and by a
   * follow, which steers rather than walking a route because the thing being
   * followed keeps moving.
   */
  movementVector(): { x: number; z: number } {
    if (this.typing) return { x: 0, z: 0 };

    let forward = 0;
    let right = 0;

    if (this.held.has('KeyW')) forward += 1;
    if (this.held.has('KeyS')) forward -= 1;
    if (this.held.has('KeyD')) right += 1;
    if (this.held.has('KeyA')) right -= 1;

    if (forward === 0 && right === 0) return { x: 0, z: 0 };

    const basis = this.camera.movementBasis();
    const x = basis.forwardX * forward + basis.rightX * right;
    const z = basis.forwardZ * forward + basis.rightZ * right;

    // Normalised, so holding two keys is not faster than holding one.
    const length = Math.hypot(x, z);
    return length > 0 ? { x: x / length, z: z / length } : { x: 0, z: 0 };
  }

  /** How fast the held arrow keys are turning the camera, in radians per second. */
  cameraTurn(): { yaw: number; pitch: number } {
    if (this.typing) return { yaw: 0, pitch: 0 };

    let yaw = 0;
    let pitch = 0;

    if (this.held.has('ArrowLeft')) yaw -= KEY_TURN_SPEED;
    if (this.held.has('ArrowRight')) yaw += KEY_TURN_SPEED;
    if (this.held.has('ArrowUp')) pitch += KEY_PITCH_SPEED;
    if (this.held.has('ArrowDown')) pitch -= KEY_PITCH_SPEED;

    return { yaw, pitch };
  }

  // -------------------------------------------------------------------------
  // Wiring
  // -------------------------------------------------------------------------

  private attach(): void {
    // pointerlockchange only exists on the document map, and wheel options only on
    // elements, so the three maps are merged rather than picking one and casting.
    type EventMap = WindowEventMap & DocumentEventMap & HTMLElementEventMap;

    const on = <K extends keyof EventMap>(
      target: Window | Document | HTMLElement,
      type: K,
      listener: (event: EventMap[K]) => void,
      options?: AddEventListenerOptions,
    ): void => {
      target.addEventListener(type, listener as EventListener, options);
      this.listeners.push(() => target.removeEventListener(type, listener as EventListener));
    };

    on(window, 'keydown', (event) => this.onKeyDown(event));
    on(window, 'keyup', (event) => this.onKeyUp(event));

    // Releasing the window mid-stride would otherwise leave a key stuck down.
    on(window, 'blur', () => {
      this.held.clear();
      this.rightButtonDown = false;
      this.touches.clear();
      this.touchStart = null;
      this.cancelLongPress();
    });

    // A finger leaving the screen at its edge, or the browser taking the gesture
    // over, both arrive here rather than as a pointerup.
    on(window, 'pointercancel', (event) => this.onTouchUp(event));

    on(this.canvas, 'pointerdown', (event) => this.onPointerDown(event));
    on(window, 'pointerup', (event) => this.onPointerUp(event));
    on(window, 'pointermove', (event) => this.onPointerMove(event));
    on(this.canvas, 'wheel', (event) => this.onWheel(event), { passive: false });

    // The browser's own menu is suppressed across the whole page, not just the
    // canvas, because the interface floats above the world and a right click on the
    // chat log or on the game menu itself would otherwise open the two on top of
    // each other. Text fields keep theirs, since copy and paste live there.
    on(window, 'contextmenu', (event) => {
      const target = event.target;
      const editable =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement);
      if (!editable) event.preventDefault();
    });
  }

  private onKeyDown(event: KeyboardEvent): void {
    this.addingToSelection = event.ctrlKey || event.metaKey;
    this.removingFromSelection = event.shiftKey;

    // Anything typed into a text field belongs to that field. The chat box says
    // so through setTyping, but the editor is full of boxes nobody told this
    // about, and a search that quietly loses its a's and d's to the movement
    // keys is a search that looks broken.
    if (isEditable(event.target)) return;

    if (event.code === 'Enter' && !this.typing) {
      event.preventDefault();
      this.events.onOpenChat();
      return;
    }

    if (this.typing) return;

    if (MOVEMENT_KEYS.has(event.code) || CAMERA_KEYS.has(event.code)) {
      this.held.add(event.code);
      event.preventDefault();
      return;
    }

    switch (event.code) {
      case 'KeyR':
        this.events.onToggleRun();
        break;

      default:
        break;
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    this.removingFromSelection = event.shiftKey;
    if (isEditable(event.target)) return;

    this.held.delete(event.code);
  }

  private onPointerDown(event: PointerEvent): void {
    // Taken from the event rather than from the keyboard, since a modifier held
    // before the page had focus never reached a key handler.
    this.addingToSelection = event.ctrlKey || event.metaKey;
    this.removingFromSelection = event.shiftKey;

    if (event.pointerType === 'touch') {
      this.onTouchDown(event);
      return;
    }

    if (event.button === 2) {
      this.rightButtonDown = true;
      this.dragged = false;
      return;
    }

    if (event.button !== 0) return;

    // The editor may want this press to be the corner of an area rather than a
    // click. It says so, and the release finishes what the press started.
    if (this.events.onAreaStart(event.clientX, event.clientY)) {
      this.drawingArea = true;
      this.canvas.setPointerCapture(event.pointerId);
      return;
    }

    this.pick(event.clientX, event.clientY);
  }

  private onPointerUp(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      this.onTouchUp(event);
      return;
    }

    if (this.drawingArea && event.button === 0) {
      this.drawingArea = false;
      this.events.onAreaEnd(event.clientX, event.clientY);
      return;
    }

    if (event.button !== 2) return;

    // Only a press that started on the world opens a world menu. A right click on
    // the chat log or the settings panel belongs to the panel.
    const startedOnWorld = this.rightButtonDown;
    this.rightButtonDown = false;

    // A right click that did not drag is a context menu, not a camera move.
    if (startedOnWorld && !this.dragged) {
      this.events.onContextMenu(event.clientX, event.clientY);
    }

    this.dragged = false;
  }

  private onPointerMove(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      this.onTouchMove(event);
      return;
    }

    if (this.drawingArea) {
      this.events.onAreaMove(event.clientX, event.clientY);
      return;
    }

    if (this.rightButtonDown) {
      if (Math.abs(event.movementX) + Math.abs(event.movementY) > 2) this.dragged = true;
      this.camera.rotate(event.movementX, event.movementY);
      return;
    }

    this.hover(event.clientX, event.clientY);
  }

  /**
   * Reports what the pointer is over, at a rate worth paying for.
   *
   * Naming the thing under the cursor means a raycast, and a mouse produces far
   * more movement than a player can read. Once every other frame is plenty.
   */
  private hover(screenX: number, screenY: number): void {
    const now = performance.now();
    if (now - this.lastHover < HOVER_INTERVAL_MS) return;
    this.lastHover = now;

    // A panel is a solid thing. The pointer resting on a bag does not reach the
    // tree behind the bag, so nothing behind it lights up or names itself in the
    // corner.
    if (coveredByInterface(screenX, screenY)) {
      this.events.onHover(-1, -1);
      return;
    }

    this.events.onHover(screenX, screenY);
  }

  // -------------------------------------------------------------------------
  // Touch
  // -------------------------------------------------------------------------
  //
  // A finger has to do the work of three buttons and a wheel, so the gestures are
  // the ones a player already knows from every other game on a phone: tap to act
  // or to walk there, hold to open the menu, drag to look, pinch to zoom.

  private onTouchDown(event: PointerEvent): void {
    this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.touches.size === 2) {
      // A second finger turns the gesture into a pinch, and whatever the first was
      // doing is abandoned rather than mixed with it.
      this.cancelLongPress();
      this.pinchDistance = this.fingerGap();
      this.dragged = true;
      return;
    }

    if (this.touches.size > 2) return;

    this.touchStart = { x: event.clientX, y: event.clientY, time: performance.now() };
    this.dragged = false;
    this.longPressFired = false;

    this.longPressTimer = window.setTimeout(() => {
      this.longPressTimer = null;
      if (this.dragged || !this.touchStart) return;
      this.longPressFired = true;
      this.events.onContextMenu(this.touchStart.x, this.touchStart.y);
    }, LONG_PRESS_MS);
  }

  private onTouchMove(event: PointerEvent): void {
    const previous = this.touches.get(event.pointerId);
    if (!previous) return;

    const dx = event.clientX - previous.x;
    const dy = event.clientY - previous.y;
    this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.touches.size >= 2) {
      const gap = this.fingerGap();
      const change = gap - this.pinchDistance;
      if (Math.abs(change) > PINCH_SLOP) {
        // Fingers apart is closer, matching every map and photo on the device.
        this.camera.zoom(-change * 2);
        this.pinchDistance = gap;
      }
      return;
    }

    if (!this.touchStart) return;

    const travelled = Math.hypot(
      event.clientX - this.touchStart.x,
      event.clientY - this.touchStart.y,
    );
    if (!this.dragged && travelled > TOUCH_DRAG_SLOP) {
      this.dragged = true;
      this.cancelLongPress();
    }

    if (this.dragged) this.camera.rotate(dx, dy);
  }

  private onTouchUp(event: PointerEvent): void {
    const start = this.touchStart;
    this.touches.delete(event.pointerId);
    this.cancelLongPress();

    if (this.touches.size > 0) {
      // Still pinching or dragging with another finger; the gesture is not over.
      this.pinchDistance = this.fingerGap();
      return;
    }

    this.touchStart = null;
    const wasDrag = this.dragged;
    const held = this.longPressFired;
    this.dragged = false;
    this.longPressFired = false;

    // A tap acts. A drag looked around, and a hold already opened the menu.
    if (!wasDrag && !held && start) this.events.onClick(start.x, start.y);
  }

  private cancelLongPress(): void {
    if (this.longPressTimer === null) return;
    window.clearTimeout(this.longPressTimer);
    this.longPressTimer = null;
  }

  /** Distance between the first two fingers, for pinch zoom. */
  private fingerGap(): number {
    const [first, second] = [...this.touches.values()];
    if (!first || !second) return this.pinchDistance;
    return Math.hypot(second.x - first.x, second.y - first.y);
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    this.camera.zoom(event.deltaY);
  }

  private pick(screenX: number, screenY: number): void {
    this.events.onClick(screenX, screenY);
  }
}

/** Whether a key press belongs to something being typed into rather than to the world. */
function isEditable(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

/**
 * Whether the interface is in the way at a point on screen.
 *
 * Asked of the page rather than worked out from a list of panels: what is on top
 * at a point is the browser's own answer, and it stays right through every layout
 * change without anything here knowing what the layout is.
 */
export function coveredByInterface(screenX: number, screenY: number): boolean {
  if (typeof document === 'undefined') return false;

  const over = document.elementFromPoint(screenX, screenY);
  if (!over) return false;

  return !(over instanceof HTMLCanvasElement);
}
